class AdapterFinder {
  constructor(root) {
    this.root = root;

    this.fitmentUrl = root.getAttribute("data-fitment-url");
    this.adapterDataUrl = root.getAttribute("data-adapter-data-url");
    this.currentProductHandle = root.getAttribute("data-current-product-handle");
    this.currentProductHasVariants = root.getAttribute("data-current-product-multi-variant") === "true";

    this.mirrorVariantField = root.querySelector("[data-af-mirror-variant-field]");
    this.mirrorVariantCardsContainer = root.querySelector("[data-af-mirror-variant-cards]");
    this.selectedMirrorVariantId = null;
    this.windshieldGroupHeading = root.querySelector("[data-af-windshield-group-heading]");
    this.rearviewGroupHeading = root.querySelector("[data-af-rearview-group-heading]");

    this.yearEl = root.querySelector("[data-af-year]");
    this.makeEl = root.querySelector("[data-af-make]");
    this.modelEl = root.querySelector("[data-af-model]");
    this.hasWindshieldButtons = root.querySelectorAll("[data-af-has-windshield-btn]");
    this.windshieldButtons = root.querySelectorAll("[data-af-windshield-btn]");
    this.modelWrapperEl = root.querySelector("[data-af-model-wrapper]");
    this.sideviewMirrorCardsContainer = root.querySelector("[data-af-sideview-mirror-cards]");
    this.crossbarCardsContainer = root.querySelector("[data-af-crossbar-cards]");
    this.sideviewMirrorCards = [];
    this.needRearviewButtons = root.querySelectorAll("[data-af-need-rearview-mirror-btn]");
    this.rearviewMirrorsContainer = root.querySelector("[data-af-rearview-cards]");
    this.needDomeLightButtons = root.querySelectorAll("[data-af-need-dome-light-btn]");
    this.windshieldMessageEl = root.querySelector("[data-af-windshield-message]");
    this.resultEl = root.querySelector("[data-af-result]");

    this.hasWindshield = null;
    this.selectedWindshieldType = null;
    this.selectedSideviewMirrorAdapter = null;
    this.needRearviewMirror = null;
    this.selectedRearviewType = null;
    this.needDomeLight = null;
    this.selectedCrossbarType = null;

    this.rearviewMirrors = null;
    this.sideviewMirrorAdapters = null;
    this.crossbarAdapters = null;

    this.fitmentIndex = null;
    this.productCache = new Map();

    this.plusIcon = `<img src="https://cdn.shopify.com/s/files/1/0400/2841/files/icon-plus.svg?v=1770403678" alt="" class="af-plus-icon">`;
    this.warningIcon = `<img src="https://cdn.shopify.com/s/files/1/0400/2841/files/icon-warn.svg?v=1770403679" alt="" class="adapter-finder__warning-icon">`;
    this.checkmarkIcon = `<img src="https://cdn.shopify.com/s/files/1/0400/2841/files/icon-check.svg?v=1770403678" alt="" class="af-checkmark-icon">`;

    this.init();
  }

  // ============================================================================
  // INITIALIZATION & SETUP
  // ============================================================================

  async init() {
    if (!this.resultEl || !this.fitmentUrl || !this.yearEl || !this.makeEl || !this.modelEl) return;

    this.renderMessage("Select a year, make, and model to see a suggestion.");

    this.populateYears({ startYear: 1990, endYear: new Date().getFullYear() });
    this.disableMakeAndModel();

    try {
      const rawFitment = await this.fetchJson(this.fitmentUrl);
      this.fitmentIndex = this.buildFitmentIndex(rawFitment);
      this.populateMakes();
      if (this.adapterDataUrl) {
        await this.loadAdapterData();
      }

      // Render the mirror variant picker (Series 1 / Series 2) when this product has more than one variant
      if (this.currentProductHasVariants && this.mirrorVariantCardsContainer) {
        await this.renderMirrorVariantCards();
      }

      // Render sideview mirror and crossbar cards from JSON
      if (this.sideviewMirrorAdapters && this.sideviewMirrorCardsContainer) {
        await this.renderSideviewMirrorCards();
        this.sideviewMirrorCards = this.sideviewMirrorCardsContainer.querySelectorAll("[data-af-sideview-mirror-card]");
      }
      if (this.crossbarAdapters && this.crossbarCardsContainer) {
        await this.renderCrossbarCards();
      }

      this.bindEvents();

      // Preload rearview mirror cards after initial render
      if (this.rearviewMirrors && this.rearviewMirrorsContainer) {
        requestAnimationFrame(() => {
          this.renderRearviewMirrorCards();
        });
      }
    } catch (e) {
      this.renderMessage("Could not load fitment data.");
    }
  }

  buildFitmentIndex(raw) {
    const index = new Map();

    for (const makeLabel of Object.keys(raw || {})) {
      const makeKey = this.normalizeKey(makeLabel);
      const modelsRaw = raw[makeLabel] || {};

      const modelMap = new Map();
      for (const modelLabel of Object.keys(modelsRaw)) {
        const modelKey = this.normalizeKey(modelLabel);
        const rules = Array.isArray(modelsRaw[modelLabel]) ? modelsRaw[modelLabel] : [];
        modelMap.set(modelKey, { label: modelLabel, rules });
      }

      index.set(makeKey, { label: makeLabel, models: modelMap });
    }

    return index;
  }

  async loadAdapterData() {
    if (!this.adapterDataUrl) return false;
    try {
      const data = await this.fetchJson(this.adapterDataUrl);
      this.rearviewMirrors = data?.["rearview-mirrors"] || null;
      this.sideviewMirrorAdapters = data?.["sideview-mirror-adapters"] || null;
      this.crossbarAdapters = data?.["crossbar-adapters"] || null;
      return true;
    } catch (_) {
      return false;
    }
  }

  // ============================================================================
  // EVENT BINDING
  // ============================================================================

  bindEvents() {
    this.yearEl.addEventListener("change", () => {
      if (!this.yearEl.value) {
        this.resetToInitialState();
        return;
      }

      this.makeEl.disabled = false;
      this.modelEl.value = "";
      this.modelEl.disabled = true;
      
      // Clear results but keep button selections
      this.clearResults();
      this.renderMessage("Select a year, make, and model to see a suggestion.");
      
      // If make is already selected, repopulate models for that make
      if (this.makeEl.value) {
        this.populateModelsForMake();
      }
    });

    this.makeEl.addEventListener("change", (event) => {
      this.modelEl.value = "";
      this.clearResults();
      if (this.modelWrapperEl) this.modelWrapperEl.style.display = "block";
      this.populateModelsForMake();
      // Hide question steps until they pick a model again (so Cab System visibility is correct for new make)
      this.updateHasWindshieldButtons(false);
      this.updateSideviewMirrorStep(false);
      if (event.target.value === "OTHER") {
        this.renderMessage("Looks like you have a custom model. These will fit many off brands not listed here. Select a model below to continue.");
      } else {
        this.renderMessage("Select a year, make, and model to see a suggestion.");
      }
    });

    this.modelEl.addEventListener("change", async () => {
      // Clear results but keep button selections
      this.clearResults();
      
      // Check if there's a match for this year/make/model before showing questions
      if (this.modelEl.value && this.yearEl.value && this.makeEl.value) {
        const matchResult = this.findAdapterMatch({ 
          year: this.yearEl.value, 
          makeKey: this.makeEl.value, 
          modelKey: this.modelEl.value 
        });
        
        // Only show questions if there's a match
        if (matchResult.match) {
          const windshieldField = this.hasWindshieldButtons[0]?.closest(".adapter-finder__field--has-windshield");
          if (windshieldField && windshieldField.style.display === "none") {
            this.updateHasWindshieldButtons(true);
          }
          this.updateBoltOnButton();
          
          // If all selections are already made, automatically resolve
          if (this.canResolve()) {
            this.resolve();
          }
        } else {
          // No match - hide questions and show message
          this.updateHasWindshieldButtons(false);
          this.updateSideviewMirrorStep(false);
          
          // Show message
          if (matchResult.yearRange) {
            const makeLabel = this.fitmentIndex.get(this.makeEl.value)?.label || this.makeEl.value;
            const model = this.fitmentIndex.get(this.makeEl.value)?.models.get(this.modelEl.value);
            const modelLabel = model?.label || this.modelEl.value;
            await this.renderNoMatchWithYearRange(makeLabel, modelLabel, matchResult.yearRange);
          } else {
            this.renderNoMatch();
          }
        }
      }
    });

    this.hasWindshieldButtons.forEach(btn => {
      const handleClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const value = btn.getAttribute("data-value");
        this.selectHasWindshield(value === "yes");
        
        this.clearResults();
        
        if (this.hasWindshield) {
          this.updateWindshieldStep(true);
        } else {
          this.updateWindshieldStep(false);
          // If they select "No" (no windshield), show sideview mirror question
          if (this.yearEl.value && this.makeEl.value && this.modelEl.value) {
            this.updateSideviewMirrorStep(true);
          }
        }
        
        // If all selections are already made, automatically resolve
        if (this.canResolve()) {
          this.resolve();
        }
      };
      btn.addEventListener("click", handleClick);
      btn.addEventListener("touchend", handleClick);
    });

    this.windshieldButtons.forEach(btn => {
      const handleClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const value = btn.getAttribute("data-value");
        this.selectWindshieldType(value);
        
        this.clearResults();
        
        // Show sideview mirror question when windshield type is selected
        if (this.yearEl.value && this.makeEl.value && this.modelEl.value) {
          this.updateSideviewMirrorStep(true);
        }
        
        // If all selections are already made, automatically resolve
        if (this.canResolve()) {
          this.resolve();
        }
      };
      btn.addEventListener("click", handleClick);
      btn.addEventListener("touchend", handleClick);
    });

    // Sideview mirror and crossbar cards attach their own click handlers in renderSideviewMirrorCards/renderCrossbarCards

    this.needRearviewButtons.forEach(btn => {
      const handleClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const value = btn.getAttribute("data-value");
        this.selectNeedRearviewMirror(value);
        this.clearResults();
        if (this.needRearviewMirror) {
          this.updateRearviewTypeStep(true);
        } else {
          this.updateRearviewTypeStep(false);
          if (this.canResolve()) this.resolve();
        }
      };
      btn.addEventListener("click", handleClick);
      btn.addEventListener("touchend", handleClick);
    });

    this.needDomeLightButtons.forEach(btn => {
      const handleClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const value = btn.getAttribute("data-value");
        this.selectDomeLight(value);
        this.clearResults();
        if (this.crossbarAdapters && this.crossbarCardsContainer) {
          this.updateCrossbarStep(true);
        }
        if (this.canResolve()) this.resolve();
      };
      btn.addEventListener("click", handleClick);
      btn.addEventListener("touchend", handleClick);
    });

  }

  // ============================================================================
  // DROPDOWN POPULATION
  // ============================================================================

  populateYears({ startYear, endYear }) {
    for (let y = endYear; y >= startYear; y--) {
      this.yearEl.appendChild(this.option(String(y), String(y)));
    }
  }

  populateMakes() {
    const makes = Array.from(this.fitmentIndex.values()).map((m) => m.label);
    const items = this.sortUnique(makes).map((label) => ({
      label,
      value: this.normalizeKey(label)
    }));
    items.sort((a, b) => {
      const aIsOther = a.value === "OTHER";
      const bIsOther = b.value === "OTHER";
      if (aIsOther && !bIsOther) return 1;
      if (!aIsOther && bIsOther) return -1;
      return (a.label || "").localeCompare(b.label || "");
    });

    this.setSelectOptions(this.makeEl, items, "Select make");
  }

  populateModelsForMake() {
    this.clearModels();

    const makeKey = this.makeEl.value;
    const make = this.fitmentIndex.get(makeKey);

    if (!make) return;

    const models = Array.from(make.models.values()).map((m) => m.label);
    const items = this.sortUnique(models).map((label) => ({
      label: makeKey === "OTHER" && label === "MODEL" ? "Any" : label,
      value: this.normalizeKey(label)
    }));

    this.setSelectOptions(this.modelEl, items, "Select model");
    this.modelEl.disabled = false;
  }

  setSelectOptions(select, items, placeholder) {
    select.innerHTML = "";
    select.appendChild(this.option("", placeholder));

    for (const item of items) {
      select.appendChild(this.option(item.value, item.label));
    }
  }

  option(value, label) {
    const opt = document.createElement("option");
    opt.value = value;
    opt.textContent = label;
    return opt;
  }

  disableMakeAndModel() {
    this.makeEl.disabled = true;
    this.modelEl.disabled = true;
  }

  clearModels() {
    this.setSelectOptions(this.modelEl, [], "Select model");
    this.modelEl.disabled = true;
  }

  // ============================================================================
  // USER SELECTION METHODS
  // ============================================================================

  setButtonGroupActive(buttons, selectedValue) {
    buttons.forEach(btn => {
      const btnValue = btn.getAttribute("data-value");
      btn.classList.toggle("active", btnValue === selectedValue);
    });
  }

  selectHasWindshield(value) {
    this.hasWindshield = value;
    this.setButtonGroupActive(this.hasWindshieldButtons, value ? "yes" : "no");
  }

  selectWindshieldType(value) {
    this.selectedWindshieldType = value;
    this.setButtonGroupActive(this.windshieldButtons, value);
    // Update message based on selection
    this.updateWindshieldMessage(value === "fixed");
  }

  selectSideviewMirrorAdapter(value) {
    this.selectedSideviewMirrorAdapter = value;
    this.setSideviewMirrorCardActive(value);
  }

  selectNeedRearviewMirror(value) {
    this.needRearviewMirror = value === "yes";
    this.setButtonGroupActive(this.needRearviewButtons, value);
  }

  selectRearviewType(value) {
    this.selectedRearviewType = value;
    this.setRearviewCardActive(value);
  }

  selectDomeLight(value) {
    this.needDomeLight = value === "yes";
    this.setButtonGroupActive(this.needDomeLightButtons, value);
  }

  // ============================================================================
  // UI STATE MANAGEMENT (SHOW/HIDE FIELDS)
  // ============================================================================

  updateHasWindshieldButtons(show) {
    const buttonField = this.hasWindshieldButtons[0]?.closest(".adapter-finder__field--has-windshield");
    if (buttonField) {
      buttonField.style.display = show ? "grid" : "none";
    }
    if (this.windshieldGroupHeading) {
      this.windshieldGroupHeading.style.display = show ? "block" : "none";
    }
    if (!show) {
      this.hasWindshield = null;
      // Reset button states
      this.hasWindshieldButtons.forEach(btn => btn.classList.remove("active"));
      this.updateWindshieldStep(false);
      this.updateSideviewMirrorStep(false);
    }
  }

  updateWindshieldStep(show) {
    const windshieldField = this.windshieldButtons[0]?.closest(".adapter-finder__field--windshield");
    if (windshieldField) {
      windshieldField.style.display = show ? "grid" : "none";
    }
    if (!show) {
      this.selectedWindshieldType = null;
      // Reset button states
      this.windshieldButtons.forEach(btn => btn.classList.remove("active"));
      this.updateWindshieldMessage(false);
      // Don't reset sideview mirror step - preserve the selection
    }
  }

  updateSideviewMirrorStep(show) {
    const sideviewMirrorField = this.sideviewMirrorCardsContainer?.closest(".adapter-finder__field--sideview-mirror");
    if (sideviewMirrorField) {
      sideviewMirrorField.style.display = show ? "grid" : "none";
    }
    if (!show) {
      this.resetSideviewMirrorSelection();
      this.updateNeedRearviewStep(false);
    } else {
      this.updateBoltOnButton();
    }
  }

  updateNeedRearviewStep(show) {
    const field = this.needRearviewButtons[0]?.closest(".adapter-finder__field--need-rearview-mirror");
    if (field) {
      field.style.display = show ? "grid" : "none";
    }
    if (this.rearviewGroupHeading) {
      this.rearviewGroupHeading.style.display = show ? "block" : "none";
    }
    if (!show) {
      this.needRearviewMirror = null;
      this.selectedRearviewType = null;
      this.needDomeLight = null;
      this.selectedCrossbarType = null;
      this.needRearviewButtons.forEach(btn => btn.classList.remove("active"));
      this.updateRearviewTypeStep(false);
      this.updateDomeLightStep(false);
      this.updateCrossbarStep(false);
    }
  }

  updateRearviewTypeStep(show) {
    const field = this.rearviewMirrorsContainer?.closest(".adapter-finder__field--rearview-mirror");
    if (field) {
      field.style.display = show ? "grid" : "none";
    }
    if (!show) {
      this.selectedRearviewType = null;
      this.needDomeLight = null;
      this.selectedCrossbarType = null;
      this.setRearviewCardActive(null);
      this.updateDomeLightStep(false);
      this.updateCrossbarStep(false);
    } else if (this.rearviewMirrorsContainer) {
      // Always ensure cards are rendered when showing (in case preload didn't run or data wasn't ready)
      if (!this.rearviewMirrorsContainer.hasChildNodes() && this.rearviewMirrors) {
        this.renderRearviewMirrorCards();
      } else if (!this.rearviewMirrors && this.adapterDataUrl) {
        this.loadRearviewDataAndRender();
      }
    }
  }

  async loadRearviewDataAndRender() {
    const ok = await this.loadAdapterData();
    if (ok && this.rearviewMirrors && this.rearviewMirrorsContainer && !this.rearviewMirrorsContainer.hasChildNodes()) {
      await this.renderRearviewMirrorCards();
    }
  }

  setRearviewCardActive(selectedValue) {
    if (!this.rearviewMirrorsContainer) return;
    this.rearviewMirrorsContainer.querySelectorAll("[data-af-rearview-mirror-card]").forEach((card) => {
      card.classList.toggle("is-active", card.getAttribute("data-value") === selectedValue);
    });
  }

  setSideviewMirrorCardActive(selectedValue) {
    if (!this.sideviewMirrorCardsContainer) return;
    this.sideviewMirrorCardsContainer.querySelectorAll("[data-af-sideview-mirror-card]").forEach((card) => {
      card.classList.toggle("is-active", card.getAttribute("data-value") === selectedValue);
    });
  }

  setCrossbarCardActive(selectedValue) {
    if (!this.crossbarCardsContainer) return;
    this.crossbarCardsContainer.querySelectorAll("[data-af-mounting-kit-crossbar-card]").forEach((card) => {
      card.classList.toggle("is-active", card.getAttribute("data-value") === selectedValue);
    });
  }

  getRearviewTypeLabel(key) {
    const labels = { "elite-series": "Elite Series", "clearview": "Clearview" };
    return labels[key] || key;
  }

  /**
   * Single card structure for sideview mirror, rearview, and crossbar.
   * @param {string} label - Card label text
   * @param {string} img - Image URL or ""
   * @param {string} dataAttr - e.g. "data-af-sideview-mirror-card"
   * @param {string} dataValue - data-value
   * @returns {HTMLElement}
   */
  createCardElement(label, img, dataAttr, dataValue, priceFormatted = null, featuresHtml = "") {
    const card = document.createElement("div");
    card.className = "adapter-finder__card";
    card.setAttribute(dataAttr, "");
    card.setAttribute("data-value", dataValue);
    const priceHtml = priceFormatted ? `<span class="adapter-finder__card__price">${this.escapeHtml(priceFormatted)}</span>` : "";
    card.innerHTML = `
      ${img ? `<img class="adapter-finder__card__img" src="${img}" alt="${this.escapeHtml(label)}">` : `<div class="adapter-finder__card__img"></div>`}
      <span class="adapter-finder__card__label">${this.escapeHtml(label)}</span>
      ${priceHtml}
      ${featuresHtml}
    `;
    return card;
  }

  // Feature callouts shown on the mirror-variant cards. Keyed by variant
  // title (lowercased) since this copy doesn't exist anywhere in Shopify's
  // product data — it's editorial, not pulled from the API. The first entry
  // is the feature every variant shares (shown plain); anything after that
  // is variant-specific and gets highlighted as a "+" addition.
  getMirrorVariantFeatures(variantTitle) {
    const MIRROR_VARIANT_FEATURES = {
      "series 1": ["Adjustable Clamp System"],
      "series 2": ["Adjustable Clamp System", "Blind Spot Coverage"]
    };
    return MIRROR_VARIANT_FEATURES[(variantTitle || "").toLowerCase().trim()] || null;
  }

  renderMirrorVariantFeaturesHtml(variantTitle) {
    const features = this.getMirrorVariantFeatures(variantTitle);
    if (!features || !features.length) return "";
    const items = features.map((feature, index) => {
      const isHighlight = index > 0;
      const text = isHighlight ? `+ ${feature}` : feature;
      return `<span class="adapter-finder__card__feature${isHighlight ? " adapter-finder__card__feature--highlight" : ""}">${this.escapeHtml(text)}</span>`;
    }).join("");
    return `<div class="adapter-finder__card__features">${items}</div>`;
  }

  async renderMirrorVariantCards() {
    if (!this.mirrorVariantCardsContainer || !this.currentProductHandle) return;

    const product = await this.fetchProductByHandle(this.currentProductHandle);
    if (!product || !product.variants || product.variants.length < 2) return;

    this.mirrorVariantCardsContainer.innerHTML = "";
    for (const variant of product.variants) {
      const label = variant.public_title || variant.title;
      const img = variant.featured_image?.src || product.featured_image || product.images?.[0] || "";
      const priceFormatted = variant.price != null ? this.moneyFromCents(variant.price) : null;
      const outOfStock = variant.available === false;
      const featuresHtml = this.renderMirrorVariantFeaturesHtml(label);
      const card = this.createCardElement(label, img, "data-af-mirror-variant-card", String(variant.id), priceFormatted, featuresHtml);
      if (outOfStock) {
        card.classList.add("adapter-finder__card--oos");
        const badge = document.createElement("span");
        badge.className = "adapter-finder__card__oos-badge";
        badge.textContent = "Out of Stock";
        card.appendChild(badge);
      }
      const handleCardClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.clearResults();
        if (outOfStock) {
          this.renderBackInStockSignup(variant, label);
          return;
        }
        this.selectMirrorVariant(variant.id);
        if (this.canResolve()) this.resolve();
      };
      card.addEventListener("click", handleCardClick);
      card.addEventListener("touchend", handleCardClick);
      this.mirrorVariantCardsContainer.appendChild(card);
    }

    if (this.mirrorVariantField) this.mirrorVariantField.style.display = "grid";
    this.setMirrorVariantCardActive(this.selectedMirrorVariantId);
  }

  selectMirrorVariant(variantId) {
    this.selectedMirrorVariantId = variantId;
    this.setMirrorVariantCardActive(variantId);
    this.yearEl.disabled = false;
  }

  setMirrorVariantCardActive(selectedValue) {
    if (!this.mirrorVariantCardsContainer) return;
    this.mirrorVariantCardsContainer.querySelectorAll("[data-af-mirror-variant-card]").forEach((card) => {
      card.classList.toggle("is-active", card.getAttribute("data-value") === String(selectedValue));
    });
  }

  async renderSideviewMirrorCards() {
    if (!this.sideviewMirrorCardsContainer || !this.sideviewMirrorAdapters) return;

    this.sideviewMirrorCardsContainer.innerHTML = "";
    for (const [key, config] of Object.entries(this.sideviewMirrorAdapters)) {
      const label = config?.label || key;
      const handle = config?.handle;
      const polarisOnly = config?.polarisOnly === true;
      const canAmMaverickX3Only = config?.canAmMaverickX3Only === true;
      let img = "";
      let priceFormatted = null;
      if (handle) {
        const product = await this.fetchProductByHandle(handle);
        img = product?.featured_image || product?.images?.[0] || "";
        const priceCents = product?.variants?.[0]?.price;
        if (priceCents != null) priceFormatted = this.moneyFromCents(priceCents);
      } else {
        priceFormatted = config?.price || null;
      }
      // Fallback to config-level image (used for non-product options like Cab System)
      if (!img && config?.image) img = config.image;
      const card = this.createCardElement(label, img, "data-af-sideview-mirror-card", key, priceFormatted);
      if (polarisOnly || canAmMaverickX3Only) {
        card.style.display = "none";
      }
      const handleCardClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.selectSideviewMirrorAdapter(key);
        this.clearResults();
        // "Coming Soon" flow: only when the option has no product handle configured.
        // Once a handle is added to adapter-data.json, the standard product flow runs.
        if (!config?.handle) {
          this.updateNeedRearviewStep(false);
          this.updateRearviewTypeStep(false);
          this.updateDomeLightStep(false);
          this.updateCrossbarStep(false);
          this.renderComingSoonSignup();
          return;
        }
        if (key && this.needRearviewButtons.length) {
          this.updateNeedRearviewStep(true);
        }
        if (this.canResolve()) this.resolve();
      };
      card.addEventListener("click", handleCardClick);
      card.addEventListener("touchend", handleCardClick);
      this.sideviewMirrorCardsContainer.appendChild(card);
    }
    this.updateBoltOnButton();
  }

  async renderCrossbarCards() {
    if (!this.crossbarCardsContainer || !this.crossbarAdapters) return;

    this.crossbarCardsContainer.innerHTML = "";
    for (const [key, config] of Object.entries(this.crossbarAdapters)) {
      const label = config?.title || key;
      const handle = config?.handle;
      let img = "";
      let priceFormatted = null;
      if (handle) {
        const product = await this.fetchProductByHandle(handle);
        img = product?.featured_image || product?.images?.[0] || "";
        const priceCents = product?.variants?.[0]?.price;
        if (priceCents != null) priceFormatted = this.moneyFromCents(priceCents);
      }
      const card = this.createCardElement(label, img, "data-af-mounting-kit-crossbar-card", key, priceFormatted);
      const handleCardClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.selectCrossbarType(key);
        this.clearResults();
        if (this.canResolve()) this.resolve();
      };
      card.addEventListener("click", handleCardClick);
      card.addEventListener("touchend", handleCardClick);
      this.crossbarCardsContainer.appendChild(card);
    }
  }

  async renderRearviewMirrorCards() {
    if (!this.rearviewMirrorsContainer || !this.rearviewMirrors) return;

    this.rearviewMirrorsContainer.innerHTML = "";
    for (const [key, config] of Object.entries(this.rearviewMirrors)) {
      const handle = config?.handle;
      if (!handle) continue;
      const product = await this.fetchProductByHandle(handle);
      const img = product?.featured_image || product?.images?.[0] || "";
      const label = this.getRearviewTypeLabel(key);
      const priceCents = product?.variants?.[0]?.price;
      const priceFormatted = priceCents != null ? this.moneyFromCents(priceCents) : null;
      const card = this.createCardElement(label, img, "data-af-rearview-mirror-card", key, priceFormatted);
      const handleCardClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.selectRearviewType(key);
        this.clearResults();
        this.updateDomeLightStep(true);
        if (this.canResolve()) this.resolve();
      };
      card.addEventListener("click", handleCardClick);
      card.addEventListener("touchend", handleCardClick);
      this.rearviewMirrorsContainer.appendChild(card);
    }
    this.setRearviewCardActive(this.selectedRearviewType);
  }

  updateDomeLightStep(show) {
    const field = this.needDomeLightButtons[0]?.closest(".adapter-finder__field--need-dome-light");
    if (field) {
      field.style.display = show ? "grid" : "none";
    }
    if (!show) {
      this.needDomeLight = null;
      this.needDomeLightButtons.forEach(btn => btn.classList.remove("active"));
      this.updateCrossbarStep(false);
    }
  }

  updateCrossbarStep(show) {
    const field = this.crossbarCardsContainer?.closest(".adapter-finder__field--mounting-kit-crossbar");
    if (field) {
      field.style.display = show && this.crossbarAdapters && Object.keys(this.crossbarAdapters).length ? "grid" : "none";
    }
    if (!show) {
      this.selectedCrossbarType = null;
      this.setCrossbarCardActive(null);
    }
  }

  selectCrossbarType(value) {
    this.selectedCrossbarType = value;
    this.setCrossbarCardActive(value);
  }

  updateBoltOnButton() {
    const makeKey = this.normalizeKey(this.makeEl?.value);
    const modelKey = this.normalizeKey(this.modelEl?.value);
    const showBoltOn = makeKey === "POLARIS" &&
                       (modelKey === "RZR" || modelKey === "GENERAL" || modelKey === "RANGER");
    const showCabSystem = makeKey === "POLARIS" && modelKey !== "RZR";
    const showMaverickX3Nor = makeKey === "CANAM" && (modelKey === "MAVERICK X3" || modelKey.startsWith("MAVERICK X3 "));

    if (this.selectedSideviewMirrorAdapter === "bolt-on" && !showBoltOn) this.selectSideviewMirrorAdapter(null);
    if (this.selectedSideviewMirrorAdapter === "maverick-x3-nor" && !showMaverickX3Nor) this.selectSideviewMirrorAdapter(null);

    this.sideviewMirrorCards.forEach(card => {
      const value = card.getAttribute("data-value");
      if (value === "bolt-on") {
        card.style.display = showBoltOn ? "block" : "none";
      } else if (value === "cab-system") {
        card.style.display = showCabSystem ? "block" : "none";
      } else if (value === "maverick-x3-nor") {
        card.style.display = showMaverickX3Nor ? "block" : "none";
      } else {
        card.style.display = "block";
      }
    });
    if (this.sideviewMirrorCardsContainer) {
      this.sideviewMirrorCardsContainer.classList.toggle("adapter-finder__cards--four-cols", showCabSystem);
    }
  }

  updateWindshieldMessage(show) {
    if (this.windshieldMessageEl) {
      if (show) {
        this.windshieldMessageEl.innerHTML = `
          <div class="adapter-finder__windshield-message-content">
            ${this.warningIcon}
            <span>FIXED WINDSHIELDS - NEED 1/8" CLEARANCE TO WORK</span>
          </div>
        `;
        this.windshieldMessageEl.style.display = "block";
      } else {
        this.windshieldMessageEl.style.display = "none";
        this.windshieldMessageEl.innerHTML = "";
      }
    }
  }

  // ============================================================================
  // STATE RESET METHODS
  // ============================================================================

  resetToInitialState() {
    this.makeEl.value = "";
    this.clearModels();
    this.disableMakeAndModel();
    this.updateHasWindshieldButtons(false);
    this.updateSideviewMirrorStep(false);
    this.updateNeedRearviewStep(false);
    this.updateCrossbarStep(false);
    this.clearResults();
    this.renderMessage("Select a year, make, and model to see a suggestion.");
  }

  resetSideviewMirrorSelection() {
    this.selectedSideviewMirrorAdapter = null;
    this.setSideviewMirrorCardActive(null);
    this.updateBoltOnButton();
  }

  clearResults() {
    this.resultEl.innerHTML = "";
  }

  // ============================================================================
  // MATCH & RESOLUTION LOGIC
  // ============================================================================

  canResolve() {
    const year = this.yearEl.value;
    const makeKey = this.makeEl.value;
    const modelKey = this.modelEl.value;

    // When this product has more than one mirror variant (e.g. Series 1 / Series 2),
    // require that choice before resolving a bundle.
    if (this.currentProductHasVariants && !this.selectedMirrorVariantId) {
      return false;
    }

    if (!year || !makeKey || !modelKey) {
      return false;
    }

    // First check if there's a match - if not, can't resolve
    const matchResult = this.findAdapterMatch({ year, makeKey, modelKey });
    if (!matchResult || !matchResult.match) {
      return false;
    }

    // Require windshield question to be answered
    if (this.hasWindshield === null) {
      return false;
    }

    // Require sideview mirror adapter selection
    if (!this.selectedSideviewMirrorAdapter) {
      return false;
    }

    // Options without a configured product handle (e.g., Cab System "coming soon")
    // can't be resolved to a product yet — skip the resolve flow.
    const selectedConfig = this.sideviewMirrorAdapters?.[this.selectedSideviewMirrorAdapter];
    if (selectedConfig && !selectedConfig.handle) {
      return false;
    }

    // If rearview question is present, require it to be answered
    if (this.needRearviewButtons.length > 0) {
      if (this.needRearviewMirror === null) return false;
      if (this.needRearviewMirror === true) {
        if (!this.selectedRearviewType || this.needDomeLight === null) return false;
      }
    }

    // If crossbar step is visible (shown after dome light), require crossbar type
    if (this.crossbarAdapters && Object.keys(this.crossbarAdapters).length) {
      const crossbarField = this.crossbarCardsContainer?.closest(".adapter-finder__field--mounting-kit-crossbar");
      if (crossbarField && crossbarField.style.display !== "none" && !this.selectedCrossbarType) {
        return false;
      }
    }

    return true;
  }

  async resolve() {
    const year = this.yearEl.value;
    const makeKey = this.makeEl.value;
    const modelKey = this.modelEl.value;

    if (!this.canResolve()) {
      return;
    }

    // Check if there's a match for this year/make/model
    const matchResult = this.findAdapterMatch({ year, makeKey, modelKey });

    if (!matchResult.match) {
      // If there's a year range, show specific message
      if (matchResult.yearRange) {
        const makeLabel = this.fitmentIndex.get(makeKey)?.label || makeKey;
        const model = this.fitmentIndex.get(makeKey)?.models.get(modelKey);
        const modelLabel = model?.label || modelKey;
        await this.renderNoMatchWithYearRange(makeLabel, modelLabel, matchResult.yearRange);
      } else {
        this.renderNoMatch();
      }
      return;
    }

    this.renderLoading();

    // Get the product handle and SKU based on make/model/sideview mirror adapter
    const productInfo = this.getProductHandleAndSku(makeKey, modelKey, this.selectedSideviewMirrorAdapter);
    
    if (!productInfo) {
      this.renderMessage("Could not determine product. Please try again.");
      return;
    }

    // Collect all product handles (current + suggested + optional rearview/dome + crossbar)
    const productHandles = new Set();
    if (this.currentProductHandle) productHandles.add(this.currentProductHandle);
    productHandles.add(productInfo.handle);

    if (this.needRearviewMirror && this.selectedRearviewType && this.rearviewMirrors) {
      const rearview = this.rearviewMirrors[this.selectedRearviewType];
      if (rearview) {
        if (this.needDomeLight && rearview.dome?.handle) {
          productHandles.add(rearview.dome.handle);
        } else if (rearview.handle) {
          productHandles.add(rearview.handle);
        }
      }
    }
    if (this.selectedCrossbarType && this.crossbarAdapters?.[this.selectedCrossbarType]?.handle) {
      productHandles.add(this.crossbarAdapters[this.selectedCrossbarType].handle);
    }

    // Fetch all products
    const bundleProducts = [];
    for (const handle of productHandles) {
      const product = await this.fetchProductByHandle(handle);
      if (product) {
        bundleProducts.push(product);
      }
    }

    // Need at least the suggested product
    const suggestedProducts = bundleProducts.filter(p => 
      !this.currentProductHandle || p.handle !== this.currentProductHandle
    );

    if (suggestedProducts.length === 0) {
      this.renderMessage("Matched an adapter, but could not load product details.");
      return;
    }

    const makeLabel = this.fitmentIndex.get(makeKey)?.label || makeKey;
    const model = this.fitmentIndex.get(makeKey)?.models.get(modelKey);
    const modelLabel = model?.label || modelKey;

    // When rearview is added, show all 4: main sideview, sideview adapter, rearview mirror, rearview crossbar
    const displayHandles = [this.currentProductHandle, productInfo.handle].filter(Boolean);
    if (this.needRearviewMirror && this.selectedRearviewType && this.rearviewMirrors) {
      const rearview = this.rearviewMirrors[this.selectedRearviewType];
      if (rearview) {
        const rearviewHandle = this.needDomeLight && rearview.dome?.handle ? rearview.dome.handle : rearview.handle;
        if (rearviewHandle) displayHandles.push(rearviewHandle);
      }
      if (this.selectedCrossbarType && this.crossbarAdapters?.[this.selectedCrossbarType]?.handle) {
        displayHandles.push(this.crossbarAdapters[this.selectedCrossbarType].handle);
      }
    }

    this.renderProducts(bundleProducts, {
      year,
      make: makeLabel,
      model: modelLabel,
      targetSku: productInfo.sku,
      adapterHandle: productInfo.handle,
      displayHandles,
      mirrorVariantId: this.selectedMirrorVariantId
    });
  }

  findAdapterMatch({ year, makeKey, modelKey }) {
    const make = this.fitmentIndex.get(makeKey);
    if (!make) return { match: false };

    const model = make.models.get(modelKey);
    if (!model) return { match: false };

    const y = Number(year);
    if (!Number.isFinite(y)) return { match: false };

    // Check all rules to find the year range
    let minYear = Infinity;
    let maxYear = -Infinity;
    let hasMatch = false;

    for (const rule of model.rules) {
      const from = Number(rule?.from);
      const to = Number(rule?.to);
      if (Number.isFinite(from) && Number.isFinite(to)) {
        minYear = Math.min(minYear, from);
        maxYear = Math.max(maxYear, to);
        if (y >= from && y <= to) {
          hasMatch = true;
        }
      }
    }

    // OTHER make has no year rules; treat as match so user can continue to options
    if (makeKey === "OTHER") {
      return { match: true };
    }

    if (hasMatch) {
      return { match: true };
    }

    // No match, but model exists - return year range
    if (Number.isFinite(minYear) && Number.isFinite(maxYear)) {
      return { match: false, yearRange: { from: minYear, to: maxYear } };
    }

    return { match: false };
  }

  // ============================================================================
  // PRODUCT & SKU LOGIC
  // ============================================================================

  getProductHandleAndSku(makeKey, modelKey, sideviewMirrorAdapter) {
    const mk = this.normalizeKey(makeKey);
    const mod = this.normalizeKey(modelKey);
    const key = sideviewMirrorAdapter;

    if (key === "maverick-x3-nor") {
      const cfg = this.sideviewMirrorAdapters?.[key];
      if (!cfg?.handle) return null;
      if (mk !== "CANAM" || !(mod === "MAVERICK X3" || mod.startsWith("MAVERICK X3 "))) return null;
      return { handle: cfg.handle, sku: cfg.sku ?? null };
    }
    if (key === "round") {
      return { handle: "sideview-round-kit-svm", sku: "ROUND-KIT-SVM" };
    }
    if (key === "profile") {
      return { handle: "sideview-profile-kit-svm", sku: "PROFILE-KIT-SVM" };
    }
    if (mk === "POLARIS" && key === "bolt-on") {
      return { handle: "sideview-bolt-on-pmirkit-1", sku: "PMIRKIT-1" };
    }

    return null;
  }

  findVariantBySku(product, sku) {
    if (!product?.variants || !sku) return null;
    return product.variants.find(v => v.sku === sku) || product.variants[0];
  }

  // ============================================================================
  // DATA FETCHING
  // ============================================================================

  async fetchJson(url) {
    const res = await fetch(url, { credentials: "same-origin" });
    if (!res.ok) throw new Error("Failed to load JSON");
    return res.json();
  }

  async fetchProductByHandle(handle) {
    if (this.productCache.has(handle)) return this.productCache.get(handle);

    const res = await fetch(`/products/${handle}.js`, { credentials: "same-origin" });
    if (!res.ok) return null;

    const data = await res.json();
    this.productCache.set(handle, data);
    return data;
  }

  // ============================================================================
  // CART OPERATIONS
  // ============================================================================

  async addMultipleToCart(items) {
    const res = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ "items": items })
    });

    if (!res.ok) throw new Error("Add to cart failed");
    return res.json();
  }

  // ============================================================================
  // RENDERING METHODS
  // ============================================================================

  renderProducts(products, { year, make, model, targetSku, adapterHandle, displayHandles, mirrorVariantId }) {

    // Without rearview: show main sideview + sideview adapter. With rearview: show all 4 (sideview, sideview adapter, rearview mirror, rearview crossbar).
    const showHandles = displayHandles && displayHandles.length > 0
      ? new Set(displayHandles)
      : new Set([this.currentProductHandle, adapterHandle].filter(Boolean));
    const displayProducts = products.filter((p) => showHandles.has(p.handle));

    // Collect cart items and total from all products (including rearview)
    const cartItems = [];
    let totalPrice = 0;
    for (const product of products) {
      let variant = null;
      if (adapterHandle && product.handle === adapterHandle && targetSku) {
        variant = this.findVariantBySku(product, targetSku);
      } else if (mirrorVariantId && product.handle === this.currentProductHandle) {
        variant = product.variants.find((v) => String(v.id) === String(mirrorVariantId)) || null;
      }
      if (!variant && product?.variants?.length) {
        variant = product.variants[0];
      }
      if (variant?.id) {
        cartItems.push({ id: variant.id, quantity: 1 });
        if (variant.price != null) totalPrice += variant.price;
      }
    }

    const totalPriceFormatted = this.moneyFromCents(totalPrice);

    // Build visible product list (all products in displayHandles: sideview, adapter, and when rearview added: rearview mirror + crossbar)
    const productsSection = displayProducts.map((product, index) => {
      let img = product?.featured_image || product?.images?.[0] || "";
      if (mirrorVariantId && product.handle === this.currentProductHandle) {
        const selectedVariant = product.variants?.find((v) => String(v.id) === String(mirrorVariantId));
        if (selectedVariant?.featured_image?.src) img = selectedVariant.featured_image.src;
      }
      const title = product?.title || "Product";

      const productHtml = `
        <div class="af-product">
          ${img ? `<img class="af-product__img" src="${img}" alt="${this.escapeHtml(title)}">` : `<div class="af-product__img"></div>`}
          <p class="af-product__title">
            <span>${this.escapeHtml(title)}</span>
          </p>
        </div>
      `;

      if (index < displayProducts.length - 1) {
        return productHtml + `<div class="af-plus">${this.plusIcon}</div>`;
      }
      return productHtml;
    }).join('');

    const hasCurrentProduct = this.currentProductHandle && displayProducts.length > 0;
    const productText = hasCurrentProduct
      ? 'These products'
      : (displayProducts.length === 1 ? 'This product' : 'These products');
    const needText = hasCurrentProduct 
      ? 'You will need all of the above products'
      : (displayProducts.length === 1 ? 'You will need this product' : 'You will need all of the above products');

    const isOther = make === "OTHER";
    const confirmationLine = isOther
      ? `${productText} may fit your <strong>${year} ${make} ${model}</strong>. Please contact customer service to confirm fitment. ${needText}.`
      : `${productText} will fit your <strong>${year} ${make} ${model}</strong>! ${needText}.`;

    this.resultEl.innerHTML = `
      <div class="af-products">
        ${productsSection}
      </div>
      <div class="af-confirmation">
        <div class="af-confirmation__icon">${this.checkmarkIcon}</div>
        <div class="af-confirmation__text">
          ${confirmationLine}
        </div>
      </div>
      ${cartItems.length > 0 ? `
        <div class="af-cart">
          <button type="button" class="af-cart__btn" data-af-add-all>
            Add To Cart - ${totalPriceFormatted}
          </button>
          <div class="af-cart__status" data-af-cart-status></div>
        </div>
      ` : ''}
    `;

    // Handle add to cart button
    const addToCartBtn = this.resultEl.querySelector("[data-af-add-all]");
    const cartStatus = this.resultEl.querySelector("[data-af-cart-status]");
    
    if (addToCartBtn && cartItems.length > 0) {
      const handleAddToCart = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (addToCartBtn.disabled) return; // Prevent double-tap
        
        addToCartBtn.disabled = true;
        if (cartStatus) {
          addToCartBtn.textContent = "Adding to cart...";
        }

        try {
          await this.addMultipleToCart(cartItems);
          if (cartStatus) {
            addToCartBtn.textContent = "Added to cart!";
          }
          // Refresh cart content and open fly-out cart drawer (theme convention)
          document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
          const cartDrawer = document.querySelector('#cart-drawer, cart-element[data-cart-drawer]');
          if (cartDrawer && typeof cartDrawer.openCartDrawer === 'function') {
            cartDrawer.openCartDrawer();
          }
        } catch (e) {
          if (cartStatus) {
            addToCartBtn.textContent = "Could not add to cart. Please try again.";
          }
        } finally {
          addToCartBtn.disabled = false;
          addToCartBtn.textContent = `Add To Cart - ${totalPriceFormatted}`;
        }
      };
      addToCartBtn.addEventListener("click", handleAddToCart);
      addToCartBtn.addEventListener("touchend", handleAddToCart);
    }
  }

  renderMessage(message) {
    this.resultEl.innerHTML = `<div class="af-message">${this.escapeHtml(message)}</div>`;
  }

  renderComingSoonSignup() {
    this.renderEmailSignupForm({
      message: "Product coming soon! Get notified by entering your email below.",
      buttonLabel: "Sign up",
      successMessage: "Thanks! We'll notify you when it's available.",
      customSource: "Adapter Finder - Cab System"
    });
  }

  renderBackInStockSignup(variant, label) {
    this.renderEmailSignupForm({
      message: `${this.escapeHtml(label)} is currently out of stock. Get notified by entering your email below.`,
      buttonLabel: "Notify Me",
      successMessage: "Thanks! We'll notify you when it's back in stock.",
      customSource: `Adapter Finder - Back In Stock - ${variant.sku || label}`
    });
  }

  renderEmailSignupForm({ message, buttonLabel, successMessage, customSource }) {
    const publicKey = this.root.getAttribute("data-klaviyo-public-key")?.trim() || "";
    const listId = this.root.getAttribute("data-klaviyo-list-id")?.trim() || "";
    this.resultEl.innerHTML = `
      <div class="af-coming-soon">
        <div class="af-coming-soon__icon">${this.checkmarkIcon}</div>
        <p class="af-coming-soon__text">${message}</p>
        <form class="af-coming-soon__form" data-af-coming-soon-form>
          <input type="email" name="email" class="af-coming-soon__input" placeholder="Your email" required aria-label="Email address" autocomplete="email">
          <button type="submit" class="af-coming-soon__btn">${this.escapeHtml(buttonLabel)}</button>
        </form>
        <p class="af-coming-soon__status" data-af-coming-soon-status aria-live="polite"></p>
      </div>
    `;
    const form = this.resultEl.querySelector("[data-af-coming-soon-form]");
    const statusEl = this.resultEl.querySelector("[data-af-coming-soon-status]");
    if (form) {
      form.addEventListener("submit", (e) => {
        e.preventDefault();
        if (!publicKey || !listId) {
          if (statusEl) statusEl.textContent = "Signup is not configured. Please add your Klaviyo public key and list ID in the theme settings.";
          return;
        }
        const emailInput = form.querySelector('input[name="email"]');
        const email = emailInput?.value?.trim();
        if (!email) return;
        const btn = form.querySelector("button[type=submit]");
        if (btn) btn.disabled = true;
        if (statusEl) statusEl.textContent = "Signing up...";
        this.subscribeToKlaviyoList(publicKey, listId, email, customSource)
          .then(() => {
            if (statusEl) statusEl.textContent = successMessage;
            if (btn) btn.disabled = false;
            form.reset();
          })
          .catch((err) => {
            if (statusEl) statusEl.textContent = err?.message || "Something went wrong. Please try again or contact us.";
            if (btn) btn.disabled = false;
          });
      });
    }
  }

  async subscribeToKlaviyoList(publicKey, listId, email, customSource = "Adapter Finder") {
    const url = `https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(publicKey)}`;
    const body = {
      data: {
        type: "subscription",
        attributes: {
          custom_source: customSource,
          profile: {
            data: {
              type: "profile",
              attributes: {
                email: email,
                subscriptions: {
                  email: {
                    marketing: { consent: "SUBSCRIBED" }
                  }
                }
              }
            }
          }
        },
        relationships: {
          list: {
            data: { type: "list", id: listId }
          }
        }
      }
    };
    const res = await fetch(url, {
      method: "POST",
      headers: {
        "revision": "2026-01-15",
        "accept": "application/vnd.api+json",
        "content-type": "application/vnd.api+json"
      },
      body: JSON.stringify(body)
    });
    if (!res.ok) {
      const errBody = await res.text();
      let errDetail = res.statusText;
      try {
        const errJson = JSON.parse(errBody);
        if (errJson?.errors?.[0]) {
          errDetail = errJson.errors[0].detail || errJson.errors[0].title || errDetail;
        }
      } catch (_) {
        if (errBody) errDetail = errBody;
      }
      throw new Error(errDetail);
    }
  }

  renderLoading() {
    this.resultEl.innerHTML = `<div class="af-loading">Loading suggestion...</div>`;
  }

  renderNoMatch() {
    this.resultEl.innerHTML = `
      <div class="af-message">
        <div>No adapter match found. Contact support to see if we can help you find the right product that will fit your machine.</div>
      </div>
    `;
  }

  renderNoMatchWithYearRange(make, model, yearRange) {
    this.resultEl.innerHTML = `
      <div class="af-message">
        <div>Currently, this product only fits <strong>${make} ${model}</strong> machines from <strong>${yearRange.from}-${yearRange.to}</strong>. Please contact support to see if we can help you find the right product that will fit your machine.</div>
      </div>
    `;
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  sortUnique(arr) {
    return Array.from(new Set(arr)).sort((a, b) => (a || "").localeCompare(b || ""));
  }

  normalizeKey(v) {
    return String(v || "")
      .trim()
      .toUpperCase()
      .replace(/\s+/g, " ")
      .replace(/-/g, "");
  }

  moneyFromCents(cents) {
    const n = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: Shopify.currency.active
      }).format(n);
    } catch (e) {
      return `$${n.toFixed(2)}`;
    }
  }

  escapeHtml(str) {
    return String(str || "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;");
  }
}

document.querySelectorAll("adapter-finder").forEach((root) => {
  new AdapterFinder(root);
});
